<template>
  <b-modal 
    id="modal-attendance-schedules" 
    centered 
    hide-footer 
    title="Attendance Report"
    size="xl"
    @hidden="onModalClose">
    <div class="modal-body-custom">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading attendance details...</p>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Download Button -->
        <div class="mb-4 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Download Attendance</h5>
          <button class="btn btn-danger btn-sm" @click="downloadAttendance">
            <i class="fe fe-download me-1"></i>
            Attendance
          </button>
        </div>

        <!-- Training Details -->
        <div class="row mb-4">
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Title</label>
              <div class="detail-value">{{ scheduleData.training_title || '-' }}</div>
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Platform</label>
              <div class="detail-value">{{ scheduleData.platform || '-' }}</div>
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Provider</label>
              <div class="detail-value">{{ scheduleData.provider || '-' }}</div>
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Training method</label>
              <div class="detail-value">{{ scheduleData.training_method || '-' }}</div>
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Quota</label>
              <div class="detail-value">{{ scheduleData.quota || '-' }}</div>
            </div>
          </div>
          <div class="col-md-3 mb-3">
            <div class="detail-item">
              <label class="detail-label">Batch</label>
              <div class="detail-value">{{ formatBatchLabel(scheduleData) }}</div>
            </div>
          </div>
        </div>

        <!-- Schedule Table -->
        <div class="table-responsive">
          <table class="table table-hover table-nowrap schedule-table">
            <thead>
              <tr>
                <th>DATE</th>
                <th>TOTAL PARTICIPANT</th>
                <th>PRESENT</th>
                <th>ABSENT</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!scheduleData.schedules || scheduleData.schedules.length === 0">
                <td colspan="5" class="text-center py-4">
                  <div class="text-muted">
                    <p class="mb-0">No schedules found</p>
                  </div>
                </td>
              </tr>
              <tr v-for="(schedule, index) in scheduleData.schedules" :key="index">
                <td>{{ formatDate(schedule.date) }}</td>
                <td>{{ schedule.total_participant || 0 }}</td>
                <td>{{ schedule.present_count || 0 }}</td>
                <td>{{ schedule.absent_count || 0 }}</td>
                <td>
                  <div class="d-flex gap-2">
                    <button 
                      class="btn btn-sm btn-info"
                      @click="viewScheduleDetail(schedule)"
                      title="View Detail"
                    >
                      <i class="fe fe-eye"></i>
                    </button>
                    <button 
                      class="btn btn-sm btn-danger"
                      @click="downloadSchedule(schedule)"
                      title="Download"
                    >
                      <i class="fe fe-download"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Employee Attendance Modal -->
    <modal-schedule-employees 
      :schedule-id="selectedScheduleId"
      @close="selectedScheduleId = null"
    />
  </b-modal>
</template>

<script>
import toast from '@/utils/toast';
import { formatBatchLabel } from '@/utils/batchIdentity';
import ModalScheduleEmployees from './ModalScheduleEmployees.vue';

export default {
  name: 'ModalAttendanceSchedules',
  components: {
    ModalScheduleEmployees
  },
  props: {
    reportId: {
      type: [Number, String],
      default: null
    },
    trainingEventId: {
      type: [Number, String],
      default: null
    },
    initialScheduleId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      selectedScheduleId: null,
      scheduleData: {
        training_title: '',
        platform: '',
        provider: '',
        training_method: '',
        quota: 0,
        batch_number: 0,
        schedules: []
      }
    };
  },
  watch: {
    reportId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchSchedules();
        }
      }
    }
  },
  methods: {
    formatBatchLabel,
    async fetchSchedules() {
      if (!this.reportId) return;

      this.loading = true;
      try {
        const response = await this.$store.dispatch('attendanceReport/fetchSchedules', this.reportId);
        
        if (response && response.data) {
          this.scheduleData = {
            training_title: response.data.training_title || '',
            platform: response.data.platform || '',
            provider: response.data.provider || '',
            training_method: response.data.training_method || '',
            quota: response.data.quota || 0,
            batch_number: response.data.batch_number || 0,
            schedules: response.data.schedules || []
          };

          if (this.initialScheduleId) {
            this.$nextTick(() => {
              this.selectedScheduleId = this.initialScheduleId;
              this.$bvModal.show('modal-schedule-employees');
            });
          }
        }
      } catch (error) {
        console.error('Error fetching schedules:', error);
        toast({ type: 'error', title: 'Error', message: 'Jadwal absensi belum berhasil dimuat' });
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    
    formatScheduleDates() {
      if (!this.scheduleData.schedules || this.scheduleData.schedules.length === 0) {
        return '-';
      }
      
      const dates = this.scheduleData.schedules.map(s => s.date).sort();
      if (dates.length === 1) {
        return this.formatDate(dates[0]);
      }
      
      const firstDate = this.formatDate(dates[0]);
      const lastDate = this.formatDate(dates[dates.length - 1]);
      
      // Group consecutive dates
      const midDates = dates.slice(1, -1);
      if (midDates.length > 0) {
        return `${firstDate} - ${this.formatDate(dates[1])}\n${this.formatDate(dates[dates.length - 2])} - ${lastDate}`;
      }
      
      return `${firstDate} - ${lastDate}`;
    },
    
    downloadAttendance() {
      if (!this.trainingEventId) {
        toast({ type: 'error', title: 'Error', message: 'Training event ID is missing' });
        return;
      }

      this.downloadReport();
    },

    async downloadReport() {
      try {
        const response = await this.$store.dispatch('attendanceReport/downloadReport', this.trainingEventId);
        
        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `attendance-${this.scheduleData.training_title || 'report'}-batch-${this.scheduleData.batch_number}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({ type: 'success', title: 'Success', message: 'Absensi berhasil diunduh' });
      } catch (error) {
        console.error('Error downloading attendance:', error);
        toast({ type: 'error', title: 'Error', message: 'Absensi belum berhasil diunduh' });
      }
    },
    
    viewScheduleDetail(schedule) {
      this.selectedScheduleId = schedule.id;
      this.$bvModal.show('modal-schedule-employees');
    },
    
    async downloadSchedule(schedule) {
      try {
        const response = await this.$store.dispatch('attendanceReport/downloadScheduleReport', schedule.id);
        
        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `schedule-${this.formatDate(schedule.date)}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({ type: 'success', title: 'Success', message: 'Jadwal berhasil diunduh' });
      } catch (error) {
        console.error('Error downloading schedule:', error);
        toast({ type: 'error', title: 'Error', message: 'Jadwal belum berhasil diunduh' });
      }
    },
    
    onModalClose() {
      this.selectedScheduleId = null;
      this.scheduleData = {
        training_title: '',
        platform: '',
        provider: '',
        training_method: '',
        quota: 0,
        batch_number: 0,
        schedules: []
      };
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-body-custom {
  padding: 1.5rem;
}

.detail-item {
  margin-bottom: 0.75rem;
}

.detail-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  display: block;
}

.detail-value {
  color: #1f2937;
  font-size: 0.85rem;
}

.badge {
  font-size: 0.7rem;
  padding: 0.3rem 0.65rem;
  font-weight: 500;
}

.badge-success {
  background-color: #10b981;
  color: white;
}

.schedule-table {
  margin-top: 1rem;
}

.schedule-table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  padding: 0.875rem 0.75rem;
  color: #6b7280;
}

.schedule-table tbody td {
  vertical-align: middle;
  padding: 0.875rem 0.75rem;
  font-size: 0.85rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-info {
  background-color: #06b6d4;
  border-color: #06b6d4;
  color: white;
}

.btn-info:hover {
  background-color: #0891b2;
  border-color: #0891b2;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #bb2d3b;
  border-color: #bb2d3b;
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.text-muted {
  color: #6b7280 !important;
}
</style>
